import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import { stringToConsistentColor } from "../../../extensions/string";
import { BrushStart } from "../../../models/chart_model";
import { RajasthanDiscomData } from "../../../models/rajasthan";
import { fetchRvpnData, getRajasthanDiscomData } from "../Utility/RestApi";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { formatDateForAPI, R_CHART_TYPES } from "../const";
import { DEMAND_UNITS } from "./Data";
import { getMonthFromDate } from "../../../extensions/date";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import { useDispatch, useSelector } from "react-redux";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { RajasthanSummaryAnalysis } from "./DiscomSummary";
import { RajasthanPeakAnalysis } from "./DiscomPeak";

const GROUP_BY = ["Discom Load", "Total Discom Load", "RVPN Drawal"];
const DISCOM = [
  {
    label: "JDVVNL",
    key: "JDVVNL",
  },
  {
    label: "JVVNL",
    key: "JVVNL",
  },
  {
    label: "AVVNL",
    key: "AVVNL",
  },
];

let Intervals = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const TYPE = ["Schedule", "Drawal", "Load Shedding", "Total Load"];

export function RajasthanDiscomAnalysis() {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[2]);
  const [selectedUnit, setSelectedUnit] = useState(DEMAND_UNITS[0]);
  const [data, setData] = useState<RajasthanDiscomData[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[0]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);

  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  const chartSuffixes = useMemo(
    () =>
      selectedGroupKeys === "RVPN Drawal"
        ? [
            {
              suffix: "rvpnl_drawal",
              displayName: "RVPN Drawal",
              stackSuffix: "rvpnl_drawal",
            },
          ]
        : [
            {
              suffix: "schedule",
              displayName: "Schedule",
              stackSuffix: "schedule",
            },
            { suffix: "drawal", displayName: "Drawal", stackSuffix: "drawal" },
            {
              suffix: "load_shedding",
              displayName: "Load Shedding",
              stackSuffix: "load_shedding",
            },
            {
              suffix: "total",
              displayName: "Total Load",
              stackSuffix: "total",
            },
          ],
    [selectedGroupKeys]
  );

  const [selectedSuffix, setSelectedSuffix] = useState([
    chartSuffixes[0],
    chartSuffixes[1],
  ]);

  useEffect(() => {
    if (selectedGroupKeys === "RVPN Drawal") {
      setSelectedSuffix([
        {
          suffix: "rvpnl_drawal",
          displayName: "RVPN Drawal",
          stackSuffix: "rvpnl_drawal",
        },
      ]);
    } else {
      setSelectedSuffix([chartSuffixes[0], chartSuffixes[1]]);
    }
  }, [chartSuffixes, selectedGroupKeys]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedInterval: { isTime: boolean },
    selectedChartType: { value: string; stackId?: string },
    ExchangeColors: string[]
  ) => {
    return {
      dataKey: `${key}_${suffix}_${selectedUnit.toLowerCase()}`,
      name: `${key.replace("_", " ").replace("undefined", "")} ${displayName}`,
      legendColor: undefined,
      stroke:
        suffix === "schedule"
          ? lightenColor(ExchangeColors[index], 25)
          : suffix == "drawal"
          ? ExchangeColors[index]
          : stringToConsistentColor(`${key}_${suffix}`),
      type: selectedChartType.value,
      stackId:
        selectedChartType.stackId !== undefined ? stackSuffix : undefined,
    };
  };

  const legends = useMemo(() => {
    return selectedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
      chartKeys.map(
        (key, index) =>
          createChartObject(
            key,
            index,
            suffix,
            displayName,
            stackSuffix,
            selectedInterval,
            selectedChartType as any,
            ExchangeColors
          ) as any
      )
    );
  }, [
    selectedSuffix,
    chartKeys,
    selectedInterval,
    selectedChartType,
    selectedUnit,
  ]);

  const [activeLegends, setActiveLegends] = useState<any[]>([]);

  useEffect(() => {
    setActiveLegends(legends);
  }, [legends]);

  const handleLegendSelect = useCallback((legends: any[]) => {
    setActiveLegends(legends);
  }, []);



  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const startDateStr = formatDateForAPI(dates.startDate);
    const endDateStr = formatDateForAPI(dates.endDate);
    const newData = await getRajasthanDiscomData(startDateStr, endDateStr);
    const rvpnData = await fetchRvpnData(startDateStr, endDateStr);

    let transformedBewData = newData.map((item) => {
      return {
        ...item,
        total_mw: item.load_shedding_mw + item.drawal_mw,
      };
    });

    let transformedRvpnData = rvpnData.map((item: any) => {
      const { drawal_mw, ...rest } = item;
      return {
        ...rest,
        rvpnl_drawal_mwh: item.drawal_mw / 4,
        rvpnl_drawal_mw: item.drawal_mw,
        rvpnl_drawal_lu: item.drawal_mw / 400,
        block: rest.slot_id,
      };
    });

    if (selectedGroupKeys === "RVPN Drawal") {
      setData([...transformedRvpnData]);
    } else {
      setData([...transformedBewData]);
    }

    setUniqueDate(
      Array.from(
        new Set(
          newData
            .map((item) => item.date)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
        )
      )
    );
    setUniqueBlock(Array.from(new Set(newData.map((item) => item.block))));
    setLoading(false);
  }, [DefaultDates, activeTab, selectedGroupKeys]);

  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys(GROUP_BY[index]);
  }

  function handleXAxisModeChange(item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  function handleUnitChange(params: string) {
    setSelectedUnit(params);
  }

  function handleDiscomChange(items: string[]) {
    setSelectedDiscom(items);
  }

  function handleTypeChange(selected: string[]) {
    if (selectedGroupKeys === "RVPN Drawal") {
      dispatch(setLoadingFilterName(""));
      return;
    }
    setSelectedSuffix(
      chartSuffixes.filter((suffixObj) =>
        selected.includes(suffixObj.displayName)
      )
    );
  }

  useEffect(() => {
    let filteredData = data.map((item) => ({
      ...item,
      month: getMonthFromDate(item.date),
    }));

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.block)
      );
    }

    if (selectedGroupKeys !== "RVPN Drawal") {
      if (selectedDiscom.length > 0) {
        filteredData = data.filter((item) =>
          selectedDiscom.includes(item.discom)
        );
      }
    }

    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((item) => item.value).includes(item.date)
      );
    }

    function getKeys() {
      if (selectedInterval.key === "time") {
        return ["date", "block"];
      } else if (selectedInterval.key === "date") {
        return ["date"];
      } else {
        return [selectedInterval.values[0]];
      }
    }
    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["date", "block", "month"],
      groupBy: [selectedGroupKeys.toLowerCase()].concat(
        selectedGroupKeys === "Discom Load" ? ["discom"] : []
      ),
      volumeKey: ``,
      aggregationKeys:
        selectedGroupKeys === "RVPN Drawal"
          ? ["rvpnl_drawal_mw", "rvpnl_drawal_mwh", "rvpnl_drawal_lu"]
          : ["schedule", "drawal", "load_shedding", "total"].map(
              (item) => item + "_" + selectedUnit.toLowerCase()
            ),
      averageKeys:
        selectedGroupKeys === "RVPN Drawal"
          ? ["rvpnl_drawal_mw"]
          : ["schedule_mw", "drawal_mw", "load_shedding_mw", "total_mw"],
      averageDuplicationKeys: ["date", "block"],
      priceKey: "",
      data: filteredData,
    };
    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [
    data,
    selectedGroupKeys,
    selectedInterval,
    selectedDiscom,
    selectedArea,
    selectedDate,
    selectedBlock,
    selectedSuffix,
    selectedUnit,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") {
      if (selectedBlock.length > 0) {
        return "block";
      }
      return "block";
    } else {
      return selectedInterval.key;
    }
  }, [selectedBlock, selectedInterval]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const summaryExtraKeys = selectedInterval.isTime
    ? ["block", selectedInterval.values[1]]
    : [selectedInterval.values[0]];

  const summaryExtraLabels = selectedInterval.values.map((value) =>
    value === "date" ? "Date" : value
  );

  const tablePar = {
    legends: legends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Discom Load",
      dates: DefaultDates[activeTab].defaultRangeDate,
    }),
  };

  function secondDataKey() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        return "date";
      }
      return "date";
    } else {
      return undefined;
    }
  }

  // Show Peak Analysis component when selected
  if (selectedXAxisMode.value === "peak") {
    return (
      <RajasthanPeakAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        selectedGroupKeys={selectedGroupKeys}
        onGroupByChange={_handleGroupByChange}
        selectedSuffix={selectedSuffix}
        onTypeChange={handleTypeChange}
        chartSuffixes={chartSuffixes}
      />
    );
  }

  // Show Summary component when selected
  if (selectedXAxisMode.value === "summary") {
    return (
      <RajasthanSummaryAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        selectedGroupKeys={selectedGroupKeys}
        onGroupByChange={_handleGroupByChange}
        selectedSuffix={selectedSuffix}
        onTypeChange={handleTypeChange}
        chartSuffixes={chartSuffixes}
      />
    );
  }

  return (
    <>
      <div className="gas-page-container">
        {loading && <Loading />}
        <NewFilterContainer>
          <div className="side-filters-container text-nowrap">
            <FilterMenu
              title="X-Axis"
              items={X_AXIS_MODES.map((item) => item.label)}
              selectedItems={[selectedXAxisMode.label]}
              onSelect={handleXAxisModeChange}
            />

            <FilterMenu
              title="Intervals"
              items={Intervals.map((item) => item.label)}
              selectedItems={[selectedInterval.label]}
              onSelect={(item, index) => {
                setSelectedInterval(Intervals[index]);
              }}
            />
            {selectedGroupKeys !== "RVPN Drawal" && (
              <FilterMenu
                title="Discom"
                items={DISCOM.map((item) => item.label)}
                selectedItems={selectedDiscom}
                onSelect={(item, index) => {}}
                setSelectedItems={(item) => {
                  if (selectedGroupKeys === "RVPN Drawal") {
                    dispatch(setLoadingFilterName(""));
                    return;
                  }
                  setSelectedDiscom(item);
                }}
              />
            )}
            <FilterMenu
              title="Unit"
              items={DEMAND_UNITS}
              selectedItems={[selectedUnit]}
              onSelect={(item, index) => {
                handleUnitChange(item);
              }}
            />
            <FilterMenu
              title="Group By"
              items={GROUP_BY}
              selectedItems={[selectedGroupKeys]}
              onSelect={(item, index) => {
                _handleGroupByChange(index);
              }}
            />
            {selectedGroupKeys !== "RVPN Drawal" && (
              <FilterMenu
                title="Type"
                items={TYPE}
                selectedItems={selectedSuffix.map((item) => item.displayName)}
                setSelectedItems={handleTypeChange}
              />
            )}
            <FilterMenu
              title="Chart Type"
              items={R_CHART_TYPES.map((item) => item.label)}
              selectedItems={[selectedChartType.label]}
              onSelect={(item, index) => {
                setSelectedChartType(R_CHART_TYPES[index]);
              }}
            />
          </div>
        </NewFilterContainer>

        <MainPageContainer>
          <SecondaryHeader />
          <div className="gas-chart-container">
            <div className="gas-heading gap-x-10">
              <h2 className="chartHeading w-60">
                {selectedGroupKeys} ({selectedUnit})
              </h2>
              <div className="flex gap-x-6  justify-end">
                <BlockRangeSelector
                  blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                  placeholder="Search Block"
                  selectedOptions={selectedBlock.map((item) => item.value)}
                  setSelectedOptions={setSelectedBlock}
                />
                <button
                  onClick={() => {
                    dataTab.label === "Chart" &&
                      downloadChartTableCSV({
                        ...tablePar,
                        legends: activeLegends,
                      });
                    dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                    dataTab.label === "Table" &&
                      downloadChartTableCSV(tablePar);
                  }}
                  className="downloadButton"
                >
                  Download Data
                </button>
              </div>
            </div>

            {dataTab.label === "Chart" && (
              <ReMixChart
                key={`${selectedGroupKeys}-${selectedUnit}-${selectedInterval.key}-${selectedChartType.value}`}
                data={chartData}
                showBrush={chartData.length > 96 ? true : undefined}
                brushStart={BrushStart.Start}
                isTimeSlot={selectedInterval.isTime}
                onlyTitle={true}
                barCategoryGap={10}
                unit={selectedUnit}
                yAxisLabel={selectedUnit}
                legends={legends}
                onLegendSelect={handleLegendSelect}
                xDataKey={xDataKey}
                xTick={
                  selectedInterval.isTime && selectedBlock.length == 0
                    ? (value: any, index: any) => {
                        return renderHourTick(value, 10);
                      }
                    : undefined
                }
                secondXDataKey={secondDataKey()}
              />
            )}
            {dataTab.label === "Summary" && (
              <LegendSummaryTable {...tablePar} />
            )}
            {dataTab.label === "Table" && <ChartTable {...tablePar} />}
          </div>
          <div className="gna-footer-container">
            <div className="grid grid-cols-3 justify-between gap-5 ">
              <Tooltip
                note={
                  <p>
                    DISCOM Energy Reports (RUVITL), Load Shedding Reports
                    (RUVITL)
                  </p>
                }
                text="1.	 Actual Drawal = Restricted Demand
           Total Load (Unrestricted Demand) = Actual Drawal + Load Shedding 
2.	 Load Shedding has been distributed across time blocks based on the actual duration of load shedding
3.	MW = Represents the average (MW) when the selected time interval is Date or Month.
"
              >
                <p className="gna-footer-text">Source</p>
              </Tooltip>
              <p className="gna-footer-text col-span-1 text-center">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </MainPageContainer>
      </div>
    </>
  );
}
